// Owner BI Dashboard — Phase 3 hotel operational stack
// Occupancy forecast, RevPAR, ADR, source mix, nationality mix, room performance.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TrendingUp, TrendingDown, Users, DollarSign, Percent, Globe, Building2,
  RefreshCw, Loader2, ArrowUpRight, Mail, Calendar, Star,
} from 'lucide-react';
import WeeklySnapshot from './WeeklySnapshot';
import CouponPipelineWidget from './CouponPipelineWidget';
import LoyaltyWallOfFame from './LoyaltyWallOfFame';
import BookingFunnelWidget from './BookingFunnelWidget';
import RecoveryStatsCard from './RecoveryStatsCard';
import SeasonDealsCard from './SeasonDealsCard';
import UpsellStatsCard from './UpsellStatsCard';
import RateWatchCard from './RateWatchCard';
import PaceRadarCard from './PaceRadarCard';
import CancellationCard from './CancellationCard';
import OverbookingGuardCard from './OverbookingGuardCard';
import SentimentRadarCard from './SentimentRadarCard';
import { BarRevenueCard } from './bar/BarRevenueCard';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const eur = (n) => `€${(Math.round(Number(n || 0) * 100) / 100).toLocaleString('en-EU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// ISO 3166-1 alpha-2 → emoji flag helper. Falls back to raw code.
const flag = (cc) => {
  if (!cc || cc.length !== 2 || cc === '??') return '🏳️';
  const A = 0x1F1E6;
  const codes = cc.toUpperCase().split('').map((c) => A + (c.charCodeAt(0) - 65));
  return String.fromCodePoint(...codes);
};

// ISO country → English name (only common ones — otherwise show code).
const COUNTRIES = {
  RS: 'Serbia', BA: 'Bosnia & Herzegovina', HU: 'Hungary', DE: 'Germany',
  AL: 'Albania', ME: 'Montenegro', HR: 'Croatia', RU: 'Russia', UA: 'Ukraine',
  PL: 'Poland', US: 'United States', GB: 'United Kingdom', FR: 'France',
  IT: 'Italy', ES: 'Spain', AT: 'Austria', CH: 'Switzerland', NL: 'Netherlands',
  BE: 'Belgium', SE: 'Sweden', NO: 'Norway', DK: 'Denmark', FI: 'Finland',
  CZ: 'Czech Republic', SK: 'Slovakia', SI: 'Slovenia', MK: 'N. Macedonia',
  BG: 'Bulgaria', RO: 'Romania', GR: 'Greece', TR: 'Turkey', IL: 'Israel',
  CA: 'Canada', AU: 'Australia', KO: 'Kosovo', XK: 'Kosovo',
};

const Kpi = ({ icon: Icon, label, value, sub, testid, color }) => {
  const map = {
    orange: 'text-orange-600 bg-orange-50',
    green: 'text-green-600 bg-green-50',
    blue: 'text-blue-600 bg-blue-50',
    purple: 'text-purple-600 bg-purple-50',
    amber: 'text-amber-600 bg-amber-50',
  };
  const c = map[color] || map.orange;
  return (
    <div className="bg-white rounded-xl border p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">{label}</p>
          <p className="text-2xl font-bold text-gray-900 leading-tight">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
      </div>
    </div>
  );
};

// Small horizontal bar for occupancy forecast rows.
const OccupancyBar = ({ pct }) => {
  const w = Math.min(100, Math.max(0, pct));
  const c = pct >= 80 ? 'bg-red-500' : pct >= 60 ? 'bg-amber-500' : pct >= 30 ? 'bg-green-500' : 'bg-blue-400';
  return (
    <div className="w-full bg-gray-100 rounded h-2 overflow-hidden">
      <div className={`${c} h-2 rounded transition-all`} style={{ width: `${w}%` }} />
    </div>
  );
};

const OwnerBI = ({ userRole }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/bi/overview?days=${days}`, {
        credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Failed to load');
      setData(json);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const kpis = data?.kpis || {};
  const window = data?.window || {};
  const forecast = data?.occupancy_forecast || [];
  const sources = data?.source_mix || [];
  const nationalities = data?.nationality_mix || [];
  const roomPerf = data?.room_performance || [];

  // Rows precomputed OUTSIDE JSX to avoid Babel plugin stack overflow.
  const forecastRows = useMemo(() => forecast.map((f) => {
    const d = new Date(f.date + 'T00:00:00');
    const dowLabel = d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
    return (
      <div key={f.date} className="grid grid-cols-12 gap-3 items-center py-2 border-b last:border-b-0 text-sm" data-testid={`bi-forecast-${f.date}`}>
        <div className="col-span-3 text-gray-800 font-medium">{dowLabel}</div>
        <div className="col-span-6"><OccupancyBar pct={f.occupancy_pct} /></div>
        <div className="col-span-1 text-right text-gray-900 font-semibold">{f.occupancy_pct}%</div>
        <div className="col-span-2 text-right text-xs text-gray-500">{f.booked_rooms}/{f.booked_rooms + f.available_rooms}</div>
      </div>
    );
  }), [forecast]);

  const SOURCE_COLORS = {
    'Direct':      'bg-emerald-500',
    'Booking.com': 'bg-blue-500',
    'Airbnb':      'bg-rose-500',
    'Expedia':     'bg-amber-500',
    'VRBO':        'bg-purple-500',
    'Agoda':       'bg-red-500',
    'Hostelworld': 'bg-orange-500',
    'TripAdvisor': 'bg-teal-500',
    'OTA':         'bg-slate-500',
  };
  const sourceColor = (s) => SOURCE_COLORS[s] || 'bg-slate-500';

  const totalGrossRev = useMemo(() => sources.reduce((s, x) => s + (x.revenue || 0), 0), [sources]);
  const totalNetRev   = useMemo(() => sources.reduce((s, x) => s + (x.net_revenue || 0), 0), [sources]);
  const totalCommission = totalGrossRev - totalNetRev;

  const sourceRows = useMemo(() => sources.map((s) => (
    <div key={s.source} className="flex items-center gap-3 py-3 border-b last:border-b-0" data-testid={`bi-source-${s.source}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${sourceColor(s.source)}`}></span>
          <p className="font-semibold text-gray-900 text-sm truncate">{s.source}</p>
          {s.commission_pct > 0 && (
            <span className="text-[9px] uppercase tracking-wider font-bold bg-red-100 text-red-700 px-1 py-0.5 rounded" title={`Estimated OTA commission`}>
              −{s.commission_pct}%
            </span>
          )}
        </div>
        <div className="w-full bg-gray-100 rounded h-1.5 mt-1.5">
          <div className={`${sourceColor(s.source)} h-1.5 rounded`} style={{ width: `${s.revenue_pct}%` }} />
        </div>
        <p className="text-[10px] text-gray-500 mt-0.5">
          {s.bookings} bookings · {s.guests} guests · {s.nights} nights · ADR {eur(s.adr)}
        </p>
      </div>
      <div className="text-right w-24">
        <p className="text-[10px] uppercase text-gray-400 font-semibold">rev/guest</p>
        <p className="text-sm font-bold text-emerald-700" data-testid={`bi-source-rpg-${s.source}`}>{eur(s.revenue_per_guest)}</p>
      </div>
      <div className="text-right w-28">
        <p className="text-[10px] uppercase text-gray-400 font-semibold">gross → net</p>
        <p className="text-sm font-semibold text-gray-800">{eur(s.revenue)}</p>
        <p className="text-xs text-emerald-700" data-testid={`bi-source-net-${s.source}`}>{eur(s.net_revenue)}</p>
      </div>
    </div>
  )), [sources]);

  const nationalityRows = useMemo(() => nationalities.map((c) => (
    <div key={c.country} className="flex items-center gap-3 py-2 border-b last:border-b-0" data-testid={`bi-country-${c.country}`}>
      <span className="text-2xl w-8">{flag(c.country)}</span>
      <div className="flex-1">
        <p className="font-medium text-gray-900 text-sm">{COUNTRIES[c.country] || c.country}</p>
        <div className="w-full bg-gray-100 rounded h-1.5 mt-1">
          <div className="bg-blue-500 h-1.5 rounded" style={{ width: `${c.pct}%` }} />
        </div>
      </div>
      <div className="text-right w-24">
        <p className="text-sm font-semibold">{c.bookings}</p>
        <p className="text-xs text-gray-500">{c.pct}%</p>
      </div>
    </div>
  )), [nationalities]);

  const roomRows = useMemo(() => roomPerf.map((r) => (
    <tr key={r.room_id} className="border-b last:border-b-0 hover:bg-gray-50" data-testid={`bi-room-${r.room_id}`}>
      <td className="py-2 pr-2 text-sm font-medium text-gray-900">{r.room_name}</td>
      <td className="py-2 pr-2 text-sm text-gray-600 text-right">{r.nights_sold}</td>
      <td className="py-2 pr-2 text-sm text-gray-600 text-right">{r.inventory || '—'}</td>
      <td className="py-2 pr-2 text-sm text-gray-600 text-right">{r.occupancy_pct}%</td>
      <td className="py-2 pr-2 text-sm font-semibold text-orange-600 text-right">{eur(r.revenue)}</td>
    </tr>
  )), [roomPerf]);

  return (
    <div className="space-y-6" data-testid="owner-bi-dashboard">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-orange-500" /> Owner BI
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {window.start_date} → {window.end_date} · Inventory: {window.inventory} rooms
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 font-medium">Window:</label>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-1.5 border rounded text-sm"
            data-testid="bi-window-select"
          >
            <option value={7}>Next 7 days</option>
            <option value={14}>Next 14 days</option>
            <option value={30}>Next 30 days</option>
            <option value={60}>Next 60 days</option>
            <option value={90}>Next 90 days</option>
            <option value={180}>Next 180 days</option>
          </select>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 bg-white border px-3 py-1.5 rounded text-sm hover:bg-gray-50"
            data-testid="bi-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
        </div>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm" data-testid="bi-error">{err}</div>
      )}

      {/* Weekly Snapshot — condensed owner digest */}
      <WeeklySnapshot />

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon={DollarSign} label="Revenue" value={eur(kpis.revenue)} sub={`over ${window.days || days} days`} color="green" testid="bi-kpi-revenue" />
        <Kpi icon={ArrowUpRight} label="ADR" value={eur(kpis.adr)} sub="avg daily rate" color="orange" testid="bi-kpi-adr" />
        <Kpi icon={Percent} label="RevPAR" value={eur(kpis.revpar)} sub="per available room" color="purple" testid="bi-kpi-revpar" />
        <Kpi icon={Users} label="Occupancy" value={`${kpis.occupancy_pct || 0}%`} sub={`${kpis.booked_room_nights || 0} / ${kpis.available_room_nights || 0} nights`} color="blue" testid="bi-kpi-occupancy" />
      </div>

      {/* Guest satisfaction */}
      <SatisfactionCard sat={data?.guest_satisfaction} windowDays={window.days || days} />
      <BarRevenueCard bar={data?.bar} />

      {/* Occupancy forecast + Source mix */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border p-5" data-testid="bi-forecast-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-orange-500" /> Occupancy forecast
            </h3>
            <span className="text-xs text-gray-500">next {window.days || days} days</span>
          </div>
          <div className="max-h-[420px] overflow-y-auto pr-1">
            {forecastRows.length === 0 ? (
              <p className="text-sm text-gray-400">No forecast data.</p>
            ) : forecastRows}
          </div>
        </div>

        <div className="bg-white rounded-xl border p-5" data-testid="bi-source-card">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-1">
            <Globe className="w-5 h-5 text-orange-500" /> Booking sources
          </h3>
          <p className="text-xs text-gray-500 mb-3">Gross vs net after estimated OTA commissions.</p>

          {/* Donut summary — visual revenue-share by source */}
          {sources.length > 0 && (
            <div className="flex items-center gap-4 mb-4 pb-4 border-b">
              <div className="relative w-24 h-24 shrink-0">
                <svg viewBox="0 0 36 36" className="w-24 h-24 -rotate-90">
                  {(() => {
                    let offset = 0;
                    const C = 2 * Math.PI * 15.9155;
                    return sources.map((s) => {
                      const dash = (s.revenue_pct || 0) * C / 100;
                      const stroke = ({
                        Direct: '#10b981', 'Booking.com': '#3b82f6', Airbnb: '#f43f5e',
                        Expedia: '#f59e0b', VRBO: '#a855f7', Agoda: '#ef4444',
                        Hostelworld: '#f97316', TripAdvisor: '#14b8a6', OTA: '#64748b',
                      })[s.source] || '#64748b';
                      const el = (
                        <circle
                          key={s.source} cx="18" cy="18" r="15.9155"
                          fill="transparent" stroke={stroke} strokeWidth="6"
                          strokeDasharray={`${dash} ${C - dash}`}
                          strokeDashoffset={-offset}
                          data-testid={`bi-donut-${s.source}`}
                        />
                      );
                      offset += dash;
                      return el;
                    });
                  })()}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center leading-tight">
                  <p className="text-[9px] uppercase text-slate-400 font-bold">Net</p>
                  <p className="text-xs font-bold text-emerald-700">{eur(totalNetRev)}</p>
                </div>
              </div>
              <div className="flex-1 min-w-0 text-xs space-y-1">
                <div className="flex justify-between text-gray-700">
                  <span>Gross revenue</span>
                  <span className="font-semibold">{eur(totalGrossRev)}</span>
                </div>
                <div className="flex justify-between text-red-600">
                  <span>− est. commission</span>
                  <span className="font-semibold" data-testid="bi-total-commission">{eur(totalCommission)}</span>
                </div>
                <div className="flex justify-between text-emerald-700 pt-1 border-t">
                  <span className="font-bold">Net to hotel</span>
                  <span className="font-bold">{eur(totalNetRev)}</span>
                </div>
              </div>
            </div>
          )}
          <div className="max-h-[420px] overflow-y-auto">
            {sourceRows.length === 0 ? (
              <p className="text-sm text-gray-400 mt-3">No source data.</p>
            ) : sourceRows}
          </div>
        </div>
      </div>

      {/* Nationality mix + Room performance */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border p-5" data-testid="bi-nationality-card">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
            <Users className="w-5 h-5 text-orange-500" /> Guest nationality (top 10)
          </h3>
          {nationalityRows.length === 0 ? (
            <p className="text-sm text-gray-400">No nationality data.</p>
          ) : nationalityRows}
        </div>

        <div className="bg-white rounded-xl border p-5" data-testid="bi-room-perf-card">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
            <Building2 className="w-5 h-5 text-orange-500" /> Room performance
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="pb-2 pr-2 text-left text-xs uppercase tracking-wide text-gray-500 font-semibold">Room</th>
                  <th className="pb-2 pr-2 text-right text-xs uppercase tracking-wide text-gray-500 font-semibold">Nights</th>
                  <th className="pb-2 pr-2 text-right text-xs uppercase tracking-wide text-gray-500 font-semibold">Units</th>
                  <th className="pb-2 pr-2 text-right text-xs uppercase tracking-wide text-gray-500 font-semibold">Occ%</th>
                  <th className="pb-2 pr-2 text-right text-xs uppercase tracking-wide text-gray-500 font-semibold">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {roomRows.length === 0 ? (
                  <tr><td colSpan="5" className="py-4 text-center text-sm text-gray-400">No room performance data.</td></tr>
                ) : roomRows}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-400 italic">
        Inventory is auto-detected from the last 12 months of booking history (max concurrent guest bookings per room type).
        Cancelled and owner-block bookings are excluded. Prices exclude taxes reported by Beds24.
      </p>

      {/* Direct-booking pipeline (coupon analytics) */}
      <CouponPipelineWidget />

      {/* Booking funnel drop-off */}
      <BookingFunnelWidget />

      <RecoveryStatsCard />
      <SeasonDealsCard />
      <PaceRadarCard />
      <CancellationCard />
      <OverbookingGuardCard userRole={userRole} />
      <SentimentRadarCard />
      <RateWatchCard />
      <UpsellStatsCard />

      {/* Loyalty Wall of Fame — top returning guests */}
      <LoyaltyWallOfFame />
    </div>
  );
};

// ---------- Guest Satisfaction Card ----------
// Rendered right under the revenue KPI row so ownership sees the "how did
// guests feel this period" number alongside the money. Everything hoisted
// out of JSX per Babel-plugin quirk.
const SatisfactionCard = ({ sat, windowDays }) => {
  if (!sat) return null;

  const emptyState = (
    <div className="bg-white rounded-xl border p-5 flex items-center gap-4" data-testid="bi-satisfaction-empty">
      <div className="w-14 h-14 rounded-full bg-slate-50 flex items-center justify-center">
        <Star className="w-7 h-7 text-slate-300" />
      </div>
      <div>
        <p className="text-sm font-semibold text-slate-900">Guest satisfaction</p>
        <p className="text-xs text-slate-500 mt-0.5">No survey responses in the last {windowDays} days yet — print more in-room QR cards to collect reviews.</p>
      </div>
    </div>
  );
  if (sat.responses === 0) return emptyState;

  const bars = Object.entries(sat.by_category || {})
    .sort((a, b) => b[1] - a[1])
    .map(([k, avg]) => (
      <div key={k} className="flex items-center gap-3">
        <span className="text-xs font-semibold text-slate-600 w-24 capitalize">{k}</span>
        <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-orange-400 to-amber-500" style={{ width: `${(avg / 5) * 100}%` }} />
        </div>
        <span className="text-xs font-bold text-slate-900 w-12 text-right">{avg.toFixed(2)}</span>
      </div>
    ));

  const overallColor = sat.overall_avg >= 4.5
    ? 'from-emerald-500 to-green-600'
    : sat.overall_avg >= 4.0 ? 'from-orange-500 to-amber-600'
    : 'from-rose-500 to-red-600';

  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="bi-satisfaction-card">
      <div className={`bg-gradient-to-br ${overallColor} text-white rounded-xl p-5 shadow`}>
        <p className="text-xs uppercase tracking-widest opacity-90 flex items-center gap-1">
          <Star className="w-3.5 h-3.5" /> Guest satisfaction
        </p>
        <p className="text-5xl font-bold mt-1" data-testid="bi-satisfaction-avg">
          {sat.overall_avg?.toFixed(2) ?? '—'}
        </p>
        <p className="text-xs opacity-90 mt-2">
          {sat.responses} response{sat.responses > 1 ? 's' : ''} · last {windowDays} days
        </p>
        {sat.best_category && (
          <p className="text-xs mt-3 bg-white/10 rounded px-2 py-1 inline-block" data-testid="bi-satisfaction-best">
            ★ {sat.best_category.name} {sat.best_category.avg.toFixed(2)}
          </p>
        )}
        {sat.worst_category && sat.worst_category.name !== sat.best_category?.name && (
          <p className="text-xs mt-1 bg-white/10 rounded px-2 py-1 inline-block ml-1" data-testid="bi-satisfaction-worst">
            ⚠ {sat.worst_category.name} {sat.worst_category.avg.toFixed(2)}
          </p>
        )}
      </div>
      <div className="md:col-span-2 bg-white rounded-xl border p-5">
        <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">By category</p>
        <div className="space-y-2">{bars}</div>
      </div>
    </div>
  );
};

export default OwnerBI;
